import { useCallback, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { clearInviteState, submitInvite } from "../../features/invite/invite.slice";
import { useAppDispatch, useAppSelector } from "../../hooks/useRedux";

interface AddFreelancerModalProps {
    show: boolean;
    onClose: () => void;
}

const AddFreelancerModal = ({ show, onClose }: AddFreelancerModalProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [inviteEmail, setInviteEmail] = useState("");
    const [invitedFriends, setInvitedFriends] = useState<string[]>([]);

    const { loading: inviteLoading } = useAppSelector(
        (state) => state.invite || { loading: false }
    );

    const handleClose = () => {
        setInviteEmail("");
        setInvitedFriends([]);
        dispatch(clearInviteState());
        onClose();
    };

    const handleAddInvite = () => {
        const email = inviteEmail.trim();

        if (!email) return;

        if (invitedFriends.includes(email)) {
            toast.warning(t("EMAIL_ALREADY_ADDED") as String);
            return;
        }

        setInvitedFriends((prev) => [...prev, email]);
        setInviteEmail("");
    };

    const handleRemoveInvite = (email: string) => {
        setInvitedFriends((prev) => prev.filter((e) => e !== email));
        dispatch(clearInviteState());
    };

    const handleSubmitInvites = useCallback(async () => {
        if (invitedFriends.length === 0) {
            toast.error(t("ADD_AT_LEAST_ONE_EMAIL") as String);
            return;
        }

        try {
            const result = await dispatch(submitInvite(invitedFriends));

            if (submitInvite.fulfilled.match(result)) {
                toast.success(t("INVITATIONS_SENT_SUCCESS") as String);
                handleClose();
            } else {
                toast.error(
                    result.error?.message || t("INVITATION_FAILED") as String
                );
            }
        } catch {
            toast.error(t("INVITATION_FAILED") as String);
        }
    }, [dispatch, invitedFriends]);

    return (
        <Modal
            show={show}
            onHide={handleClose}
            centered
            className="invite-friends-modal"
        >
            <Modal.Header className="invite-modal-header">
                <Modal.Title className="invite-modal-title">
                    {t("INVITE_FREELANCERS")}
                </Modal.Title>

                <button className="invite-modal-close" onClick={handleClose}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
            </Modal.Header>

            <Modal.Body className="invite-modal-body">
                <p className="invite-modal-description">
                    {t("INVITE_FREELANCERS_WORKING_WITH_YOU")}
                </p>

                {invitedFriends.map((email) => (
                    <div key={email} className="invited-friend-item">
                        <span className="invited-friend-email">{email}</span>
                        <button
                            className="remove-invite-btn"
                            onClick={() => handleRemoveInvite(email)}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M16 12H8M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="#E33B32" stroke-width="2" stroke-linecap="round" />
                            </svg>
                        </button>
                    </div>
                ))}

                <div className="invite-input-section">
                    <label className="invite-input-label">
                        {t("INVITE_FREELANCER")}
                    </label>

                    <div className="invite-input-wrapper">
                        <Form.Control
                            type="email"
                            placeholder={t("ENTER_FREELANCER_EMAIL")}
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                        />
                        {/* <svg className="email-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" />
                            <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" />
                        </svg> */}
                        <Button
                            className="invite-add-btn "
                            onClick={handleAddInvite}
                        >
                            {t("ADD")}
                        </Button>
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer className="invite-modal-footer">
                <Button
                    className="invite-cancel-btn"
                    variant="outline-primary"
                    onClick={handleClose}
                    disabled={inviteLoading}
                >
                    {t("CANCEL")}
                </Button>

                <Button
                    className="invite-submit-btn"
                    variant="primary"
                    onClick={handleSubmitInvites}
                    disabled={inviteLoading}
                >
                    {inviteLoading ? t("SENDING") : t("SUBMIT")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AddFreelancerModal;
